import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";

export default function MailClubInscription() {
  const nav = useNavigate();
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "",
    address_line1: "", address_line2: "", postal_code: "", city: "", country: "France",
    accept_cgv: false, accept_marketing: false,
  });
  const [loading, setLoading] = useState(false);

  function set(k, v) { setF(prev => ({ ...prev, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!f.accept_cgv) return toast.error("Merci d'accepter les CGV et la politique de confidentialité.");
    if (!f.first_name || !f.last_name || !f.email) return toast.error("Prénom, nom et email requis.");
    if (!f.address_line1 || !f.postal_code || !f.city) return toast.error("Adresse postale requise.");
    setLoading(true);
    try {
      const r = await api.post("/mailclub/signup", { ...f, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
      setLoading(false);
    }
  }

  return (
    <div data-testid="mailclub-inscription-page" className="max-w-3xl mx-auto px-6 md:px-12 py-16">
      <Link to="/club-de-lettres" className="text-sm text-[#A3876A] hover:underline inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14}/> Retour
      </Link>
      <p className="font-hand text-xl text-[#A3876A] mb-2">Encore quelques mots</p>
      <h1 className="font-serif text-3xl md:text-4xl mb-3">Rejoindre le Club de Lettres</h1>
      <p className="text-[#4A4036] mb-10 italic">
        Ton adresse postale me permet de préparer ton courrier chaque mois. Elle reste chez moi — jamais partagée, jamais visible pour les autres.
      </p>

      <form onSubmit={submit} className="space-y-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Prénom *</label>
            <input data-testid="mc-first-name" required value={f.first_name} onChange={e => set("first_name", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Nom *</label>
            <input data-testid="mc-last-name" required value={f.last_name} onChange={e => set("last_name", e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Email *</label>
          <input data-testid="mc-email" required type="email" value={f.email} onChange={e => set("email", e.target.value)}
            className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
        </div>

        <div className="border-t border-[#E8DCC8] pt-6">
          <p className="font-hand text-lg text-[#A3876A] mb-4">Ton adresse postale</p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Adresse *</label>
              <input data-testid="mc-line1" required value={f.address_line1} onChange={e => set("address_line1", e.target.value)}
                placeholder="12 rue des Écrivains" className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Complément</label>
              <input data-testid="mc-line2" value={f.address_line2} onChange={e => set("address_line2", e.target.value)}
                placeholder="Bâtiment B, appartement 4" className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Code postal *</label>
                <input data-testid="mc-cp" required value={f.postal_code} onChange={e => set("postal_code", e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Ville *</label>
                <input data-testid="mc-city" required value={f.city} onChange={e => set("city", e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#7C7064] mb-2">Pays</label>
              <input data-testid="mc-country" value={f.country} onChange={e => set("country", e.target.value)}
                className="w-full px-4 py-3 bg-white border border-[#E8DCC8] outline-none focus:border-[#A3876A]"/>
            </div>
          </div>
        </div>

        <div className="border-t border-[#E8DCC8] pt-6 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="mc-cgv" type="checkbox" required checked={f.accept_cgv} onChange={e => set("accept_cgv", e.target.checked)}
              className="mt-1 accent-[#A3876A]"/>
            <span className="text-sm text-[#4A4036] leading-relaxed">
              J'accepte les <Link to="/cgv" className="underline hover:text-[#A3876A]">conditions générales de vente</Link> et la <Link to="/confidentialite" className="underline hover:text-[#A3876A]">politique de confidentialité</Link>. *
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="mc-marketing" type="checkbox" checked={f.accept_marketing} onChange={e => set("accept_marketing", e.target.checked)}
              className="mt-1 accent-[#A3876A]"/>
            <span className="text-sm text-[#4A4036] leading-relaxed">
              Je souhaite recevoir les actualités et nouveautés des RDV de l'écriture (optionnel).
            </span>
          </label>
        </div>

        <button data-testid="mc-submit" disabled={loading} type="submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#2C2621] text-[#F5F0E6] px-8 py-4 hover:bg-[#1a1613] transition-colors disabled:opacity-60">
          <Sparkles size={18}/>
          {loading ? "Redirection vers le paiement…" : "Continuer vers le paiement sécurisé — 12€/mois"}
        </button>

        <p className="text-xs text-center text-[#7C7064] italic">
          Paiement 100% sécurisé via Stripe. Ta carte n'est jamais stockée sur notre site.<br/>
          Aucun engagement — résiliable à tout moment depuis ton espace personnel.
        </p>
      </form>
    </div>
  );
}
